/**
 * m3 echo Frontend - Protected Route Component
 *
 * Wrapper component that ensures user is authenticated before showing content.
 * Redirects to login if not authenticated.
 *
 * Security improvements (v4.5.0):
 * - Attempts to restore session from HttpOnly cookie on page load
 * - No localStorage dependency - uses in-memory auth state
 *
 * Early Access Disclaimer (v4.7.0):
 * - Checks if user needs to accept disclaimer after authentication
 * - Shows disclaimer popup on first login and daily thereafter
 * - User must accept to proceed or cancel to logout
 *
 * Maintenance Mode:
 * - Shows maintenance page for non-admin users when maintenance mode is active
 * - Admin users can access the system normally
 */

import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { Box, CircularProgress, Typography, Button } from '@mui/material';
import EngineeringIcon from '@mui/icons-material/Engineering';
import { useAuthStore } from '../stores/useAuthStore';
import { authService } from '../services/auth';
import { EarlyAccessDisclaimerDialog } from './EarlyAccessDisclaimerDialog';
import type { DisclaimerStatus } from '../types/api';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const {
    isAuthenticated,
    isInitialized,
    setAuth,
    setInitialized,
    clearAuth,
    user,
    accessToken,
    maintenanceMode
  } = useAuthStore();
  const mustChangePassword = user?.must_change_password || false;
  const [isChecking, setIsChecking] = useState(true);
  const [retrying, setRetrying] = useState(false);

  // Disclaimer state
  const [disclaimerStatus, setDisclaimerStatus] = useState<DisclaimerStatus | null>(null);
  const [showDisclaimer, setShowDisclaimer] = useState(false);
  const [disclaimerChecked, setDisclaimerChecked] = useState(false);

  // On mount, try to restore session from HttpOnly cookie
  useEffect(() => {
    async function checkSession() {
      // Skip if already initialized
      if (isInitialized) {
        setIsChecking(false);
        return;
      }

      try {
        // Try to refresh using the HttpOnly cookie
        // If cookie exists and is valid, we'll get back user data
        const response = await authService.checkSession();

        if (response) {
          // Session restored successfully
          console.log('[ProtectedRoute] Session restored from cookie');
          setAuth(response);
        } else {
          // No valid session
          console.log('[ProtectedRoute] No valid session found');
          clearAuth();
        }
      } catch (error) {
        // No valid session cookie
        console.log('[ProtectedRoute] Session check failed:', error);
        clearAuth();
      } finally {
        setInitialized(true);
        setIsChecking(false);
      }
    }

    checkSession();
  }, [isInitialized, setAuth, setInitialized, clearAuth]);

  // Reset disclaimer check when mustChangePassword changes to false
  // This ensures disclaimer is checked AFTER password change flow completes
  useEffect(() => {
    if (!mustChangePassword && disclaimerChecked) {
      // User just completed password change, re-check disclaimer
      setDisclaimerChecked(false);
    }
  }, [mustChangePassword]);

  // Check disclaimer status after authentication
  // Skip during maintenance mode to avoid 503 errors marking disclaimerChecked=true
  useEffect(() => {
    async function checkDisclaimer() {
      // Only check if authenticated, not checking password, not in maintenance, and have a token
      if (!isAuthenticated || mustChangePassword || !accessToken || disclaimerChecked || maintenanceMode) {
        return;
      }

      try {
        console.log('[ProtectedRoute] Checking disclaimer status...');
        const status = await authService.getDisclaimerStatus(accessToken);
        setDisclaimerStatus(status);

        if (status.disclaimer_required) {
          console.log('[ProtectedRoute] Disclaimer required, showing dialog');
          setShowDisclaimer(true);
        } else {
          console.log('[ProtectedRoute] Disclaimer not required');
        }
      } catch (error) {
        console.error('[ProtectedRoute] Failed to check disclaimer status:', error);
        // If we can't check, proceed anyway (don't block the user)
      } finally {
        setDisclaimerChecked(true);
      }
    }

    checkDisclaimer();
  }, [isAuthenticated, mustChangePassword, accessToken, disclaimerChecked, maintenanceMode]);

  // Handle disclaimer acceptance
  const handleAcceptDisclaimer = async () => {
    if (!accessToken) return;

    await authService.acceptDisclaimer(accessToken);
    console.log('[ProtectedRoute] Disclaimer accepted');
    setShowDisclaimer(false);

    // Update user state to reflect acceptance - refresh from server
    if (user) {
      try {
        const response = await authService.getCurrentUser(accessToken);
        setAuth(response);
      } catch {
        // If refresh fails, just continue - the user has accepted
      }
    }
  };

  // Handle disclaimer cancellation (logout)
  const handleCancelDisclaimer = async () => {
    console.log('[ProtectedRoute] Disclaimer cancelled, logging out');
    setShowDisclaimer(false);

    try {
      if (accessToken) {
        await authService.logout(accessToken);
      }
    } catch (error) {
      console.error('[ProtectedRoute] Logout failed:', error);
    } finally {
      clearAuth();
    }
  };

  // Handle maintenance retry
  const handleRetry = async () => {
    setRetrying(true);
    try {
      const response = await authService.checkSession();
      if (response) {
        setAuth(response);
      }
    } catch {
      // Still in maintenance or session expired
    } finally {
      setRetrying(false);
    }
  };

  // Show loading spinner while checking session
  if (isChecking || !isInitialized) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!isAuthenticated) {
    // Not logged in, redirect to login page
    return <Navigate to="/login" replace />;
  }

  // Maintenance mode - show maintenance page for non-admin users
  if (maintenanceMode && !user?.is_admin) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          gap: 2,
          p: 3,
          textAlign: 'center',
        }}
      >
        <EngineeringIcon sx={{ fontSize: 80, color: 'text.secondary', mb: 1 }} />
        <Typography variant="h4" fontWeight={700}>
          Wartung
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 400 }}>
          m3 echo ist bald wieder verfügbar.
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
          <Button
            variant="contained"
            onClick={handleRetry}
            disabled={retrying}
          >
            {retrying ? <CircularProgress size={20} sx={{ mr: 1 }} /> : null}
            Erneut versuchen
          </Button>
          <Button
            variant="outlined"
            onClick={() => clearAuth()}
          >
            Abmelden
          </Button>
        </Box>
      </Box>
    );
  }

  if (mustChangePassword) {
    // Must change password, redirect to password change page
    return <Navigate to="/change-password" replace />;
  }

  // Onboarding check - redirect to onboarding if not completed.
  // The /onboarding route itself uses a lighter auth guard to avoid a loop.
  if (user && !user.onboarding_completed) {
    return <Navigate to="/onboarding" replace />;
  }

  // Show disclaimer dialog if required
  if (showDisclaimer && disclaimerStatus) {
    return (
      <>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '100vh',
            backgroundColor: 'background.default',
          }}
        >
          <CircularProgress />
        </Box>
        <EarlyAccessDisclaimerDialog
          open={showDisclaimer}
          headline={disclaimerStatus.disclaimer_headline}
          text={disclaimerStatus.disclaimer_text}
          onAccept={handleAcceptDisclaimer}
          onCancel={handleCancelDisclaimer}
        />
      </>
    );
  }

  // Show loading while checking disclaimer
  if (isAuthenticated && !disclaimerChecked) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  // Authenticated, password is fine, and disclaimer is handled - show the protected content
  return <>{children}</>;
};
